/**
 * Data layer for `sub-network-creation`. Thin typed fetch wrappers over /api/v1/sub-network-creation.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface SubNetworkCreationRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListSubNetworkCreationResponse {
	count: number;
	items: SubNetworkCreationRow[];
}

export interface CreateSubNetworkCreationBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/sub-network-creation";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListSubNetworkCreationResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListSubNetworkCreationResponse>(res);
}

export async function create(body: CreateSubNetworkCreationBody): Promise<SubNetworkCreationRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<SubNetworkCreationRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const subNetworkCreationQueryKeys = {
	all: ["sub-network-creation"] as const,
	list: () => [...subNetworkCreationQueryKeys.all] as const,
	detail: (id: string) => [...subNetworkCreationQueryKeys.all, id] as const,
};
